// @flow
import React, { Component, Fragment } from 'react';
import numeral from 'numeral';
import qs from 'qs';
import isEqual from 'lodash.isequal';
import moment from 'moment';
import { Route } from 'react-router';
import { Link } from 'react-router-dom';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/styles';
import { Paper } from '@material-ui/core';
import type { Dispatch } from 'redux';
import type { $AxiosError } from 'axios';
import type { Connector } from 'react-redux';
import type { TFunction } from 'react-i18next';
import type { ContextRouter } from 'react-router';
import FilterToolbar from './containers/FilterToolbar';
import SearchToolbar from './containers/SearchToolbar';
import ActionToolbar from './containers/ActionToolbar';
import LoadingPage from '../../components/LoadingPage';
import SimplePane from '../../components/SimplePane';
import DataTable from '../../components/DataTable';
import { format } from '../../../browser/shared/util/gigwalkApiErrorUtil';
import * as snackbar from '../../ducks/snackbar';
import { actions, selectors } from './duck';
import styles from './styles';
import type { State as RootState } from '../../redux/initialState';
import getDisplayName from '../../util/getDisplayName';

type State = {
    loading: boolean,
};

type OwnProps = ContextRouter & {
    classes: Object,
    t: TFunction,
};

type StateProps = {
    data: Object[],
    fetching: boolean,
    filters: {
        customer_id: ?number,
        receiver_email: ?string,
        ticket_id: ?number,
        paypal_transaction_id: ?string,
    },
    limit: number,
    offset: number,
    pages: number,
    recordCount: number,
    search: {
        page?: string,
        q?: string,
        size?: string,
    },
    sort: Object[],
    user: ?Object,
};

type DispatchProps = {
    enqueueSnackbar: (message: string, options: Object) => void,
    fetchData: (filters: ?Object, sort: ?Object[], limit: ?number, offset: ?number) => Promise<void>,
    loadFilters: (search: Object) => Promise<void>,
};

type Props = OwnProps & StateProps & DispatchProps;

export class PayoutList extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        // this._fetchData = debounce(this._fetchData, 500);
        this.state = {
            loading: true,
        };
    }

    componentDidMount() {
        const { filters, limit, loadFilters, offset, search, sort } = this.props;
        const promises = [
            this._fetchData(filters, sort, limit, offset),
            loadFilters(search),
        ];

        Promise.all(promises)
            .then(() => { this.setState({ loading: false }); });
    }

    componentWillReceiveProps(nextProps: Props) {
        const { filters, limit, offset, sort } = this.props;
        const {
            filters: nextFilters,
            limit: nextLimit,
            offset: nextOffset,
            sort: nextSort,
        } = nextProps;

        // If any filter, sort, or pagination parameters changed, we should fetch new data.
        const shouldFetchData = !isEqual(nextFilters, filters)
            || !isEqual(nextSort, sort)
            || nextLimit !== limit
            || nextOffset !== offset;

        if (shouldFetchData) {
            this._fetchData(nextFilters, nextSort, nextLimit, nextOffset);
        }
    }

    handlePageChange = (page: number) => {
        const { location, history, search } = this.props;
        const query = page >= 0 ? { page: page + 1 } : {}; // Don't forget to convert from zero-based index!

        history.replace({
            pathname: location.pathname,
            search: qs.stringify(
                { ...search, ...query },
                { addQueryPrefix: true, encodeValuesOnly: true }
            ),
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        const { location, history, search } = this.props;
        const { size, ...query } = search;

        history.replace({
            pathname: location.pathname,
            search: qs.stringify(
                pageSize ? { ...query, size: pageSize } : query,
                { addQueryPrefix: true, encodeValuesOnly: true }
            ),
        });
    };

    _fetchData = (filters: ?Object, sort: ?Object[], limit: ?number, offset: ?number) => {
        const { enqueueSnackbar, fetchData } = this.props;
        return fetchData(filters, sort, limit, offset)
            .catch((err: $AxiosError<any>) => {
                const resp = err ? err.response : null;
                if (resp && resp.data && resp.data.gw_api_response) {
                    const message = format(resp.data.gw_api_response);
                    enqueueSnackbar(message, { variant: 'error' });
                }
            });
    };

    _getColumns() {
        const { classes, t, user } = this.props;
        const userOrgId = user ? user.organization.id : 0;

        const status = {
            sortable: false,
            Header: t('payoutList.status'),
            accessor: 'status',
            className: classes.status,
            width: 100,
        };
        const ticketId = {
            sortable: false,
            Header: t('payoutList.ticket'),
            accessor: 'ticket',
            minWidth: 150,
            Cell: (props: Object) => {
                const ticket = props.value;
                return (
                    <Link to={`/tickets/${userOrgId}/info/${ticket.id}`}>
                        <strong style={{ fontWeight: 600 }}>{ticket.title}</strong>
                        <div>{`ID: ${ticket.id}`}</div>
                    </Link>
                );
            },
        };
        const transactionId = {
            sortable: false,
            Header: t('payoutList.transactionId'),
            accessor: 'transactionId',
            minWidth: 120,
        };
        const customer = {
            sortable: false,
            Header: t('payoutList.customer'),
            accessor: 'customer',
            className: classes.customer,
            Cell: (props: Object) => getDisplayName(props.value),
        };
        const paypalEmail = {
            sortable: false,
            Header: t('payoutList.paypalEmail'),
            accessor: 'paypalEmail',
        };
        const paymentDate = {
            sortable: false,
            Header: t('payoutList.paymentDate'),
            accessor: 'paymentDate',
            className: classes.datetime,
            Cell: (props: Object) => {
                const date = moment(props.value);
                if (!date.isValid()) {
                    return null;
                }
                return (
                    <Fragment>
                        <div>{date.format('MMM DD YYYY')}</div>
                        <div>{date.format('hh:mm A')}</div>
                    </Fragment>
                );
            },
        };
        const amount = {
            sortable: false,
            Header: t('payoutList.amount'),
            accessor: 'amount',
            width: 100,
            Cell: (props: Object) => numeral(props.value).format('$0,0.00'),
        };

        return [ticketId, customer, amount, status, paypalEmail, transactionId, paymentDate];
    }

    render() {
        const { loading } = this.state;
        const { classes, data, fetching, pages, recordCount, search } = this.props;
        const page = Math.max(parseInt(search.page, 10) - 1, 0) || 0;
        const pageSize = Math.max(parseInt(search.size, 10), 0) || 10;
        const columns = this._getColumns();

        if (loading) {
            return (
                <SimplePane>
                    <LoadingPage />
                </SimplePane>
            );
        }

        return (
            <SimplePane>
                <div className={classes.toolbars}>
                    <Route render={(props: ContextRouter) => <SearchToolbar {...props} className={classes.search} />} />
                    <Route render={(props: ContextRouter) => <FilterToolbar {...props} className={classes.filters} />} />
                </div>
                <Paper elevation={2}>
                    <Route component={ActionToolbar} />
                    <DataTable
                      columns={columns}
                      count={recordCount}
                      data={data}
                      loading={fetching}
                      onPageChange={this.handlePageChange}
                      onPageSizeChange={this.handlePageSizeChange}
                      pageSizeOptions={[10, 20, 50]}
                      defaultPageSize={10}
                      page={page}
                      pages={pages}
                      pageSize={pageSize}
                    />
                </Paper>
            </SimplePane>
        );
    }
}

const mapStateToProps = (state: RootState, props: OwnProps): StateProps => {
    const {
        limit,
        offset,
        sort_field: sortField,
        sort_order: sortOrder,
        ...filters
    } = selectors.getAPIParams(state, props);

    return {
        data: selectors.getTableData(state, props),
        fetching: state.payoutList.fetching,
        filters,
        limit,
        offset,
        pages: state.payoutList.metadata.page_count || 0,
        recordCount: state.payoutList.metadata.record_count,
        search: selectors.parseSearchParams(state, props),
        sort: [{ [sortField]: sortOrder }],
        user: state.session.user,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
    enqueueSnackbar: (message: string, options: Object) => dispatch(snackbar.actions.enqueue(message, options)),
    fetchData: (filters: ?Object, sort: ?Object[], limit: ?number, offset: ?number) => (
        dispatch(actions.fetchData(filters, sort, limit, offset))
    ),
    loadFilters: (search: Object) => dispatch(actions.loadFilters(search)),

});

const connector: Connector<OwnProps, Props> = connect(mapStateToProps, mapDispatchToProps);
const enhance = compose(
    withStyles(styles, { name: 'PayoutList' }),
    withTranslation(),
    connector
);

export default enhance(PayoutList);
